import { useEffect, useState } from 'react';
import { RefreshCw, ArrowDownToLine, ArrowUpFromLine, Handshake } from 'lucide-react';
import { useSocial } from '../hooks/useSocial';
import { getVariantConfig } from '../utils/cardVariants';
import { ensurePricesLoaded, variantToPrice, formatCurrency } from '../utils/portfolioValue';
import { setIdFromCardId } from '../utils/cardIndex';

const FALLBACK_IMG =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA0OCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxjaXJjbGUgY3g9IjI0IiBjeT0iMzIiIHI9IjEwIiBmaWxsPSIjRDFENURCIi8+Cjwvc3ZnPg==';

const PRIORITY_STYLES = {
  high: 'bg-danger-bg border-danger-border text-brick',
  medium: 'bg-yellow/15 border-yellow/40 text-yellow-dark',
  low: 'bg-cream-bg border-border text-ink-secondary'
};

const initials = (username) => (username ? username.slice(0, 2).toUpperCase() : '?');

const MatchCard = ({ cardItem }) => {
  const config = getVariantConfig(cardItem.variant);
  const price = variantToPrice(cardItem.cardId, cardItem.variant);

  return (
    <div className="bg-cream-bg border border-border rounded-xl p-2.5 flex items-center gap-2.5">
      <img
        src={cardItem.cardImage || FALLBACK_IMG}
        alt={cardItem.cardName}
        className="w-9 h-12 object-contain rounded flex-shrink-0"
        onError={(e) => {
          e.target.src = FALLBACK_IMG;
        }}
      />
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-semibold text-ink-primary truncate">
          {cardItem.cardName || cardItem.cardId}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={`${config.color} text-white text-[8px] font-bold rounded px-1 py-0.5`}>
            {config.short}
          </span>
          <span className="text-[10px] text-ink-secondary truncate">
            {cardItem.setName} #{cardItem.cardNumber} • ×{cardItem.tradeableQty}
          </span>
        </div>
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        {cardItem.priority && (
          <span
            className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${
              PRIORITY_STYLES[cardItem.priority] || PRIORITY_STYLES.medium
            }`}
          >
            {cardItem.priority.toUpperCase()}
          </span>
        )}
        {price > 0 && <span className="text-[10px] text-ink-muted">≈ {formatCurrency(price)}</span>}
      </div>
    </div>
  );
};

const MatchesTab = () => {
  const { matches, matchesLoaded, refreshMatches, friends, openProposalBuilder } = useSocial();
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(null); // friend id

  // Load prices for matched cards (non-blocking — values fill in as sets resolve)
  const [, setPricesTick] = useState(0);
  useEffect(() => {
    const setIds = [
      ...new Set(
        matches.flatMap((m) => [...m.theyHave, ...m.theyWant].map((c) => setIdFromCardId(c.cardId)))
      )
    ];
    if (setIds.length === 0) return;
    let cancelled = false;
    ensurePricesLoaded(setIds).then(() => {
      if (!cancelled) setPricesTick((t) => t + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [matches]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshMatches();
    setRefreshing(false);
  };

  if (!matchesLoaded) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-navy-dark border-t-transparent"></div>
      </div>
    );
  }

  const activeMatches = matches.filter((m) => m.score > 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[12px] text-ink-secondary">
          Your wishlist matched against your friends' duplicates — and theirs against yours.
        </p>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-1.5 text-[12px] font-semibold text-navy-dark bg-white border border-border px-3 py-1.5 rounded-lg hover:bg-cream-bg transition-colors flex-shrink-0 ml-3"
        >
          <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {activeMatches.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 text-center bg-white border border-border rounded-2xl">
          <div className="w-12 h-12 rounded-full bg-cream-bg border border-border flex items-center justify-center mb-3">
            <Handshake size={20} className="text-ink-faint" />
          </div>
          <p className="text-[13px] text-ink-secondary max-w-sm px-4">
            {friends.length === 0
              ? 'Add some friends first — matches appear when a friend has a card you want, or wants one of your spares.'
              : 'No matches yet. Star cards on your wishlist and keep your collection up to date — matches show up automatically.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {activeMatches.map((m) => (
            <div key={m.friend.id} className="bg-white border border-border rounded-2xl overflow-hidden">
              <button
                onClick={() => setExpanded(expanded === m.friend.id ? null : m.friend.id)}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-cream-bg/50 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow to-brick flex items-center justify-center text-navy-dark text-sm font-heading font-bold flex-shrink-0">
                  {initials(m.friend.username)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-ink-primary">{m.friend.username}</div>
                  <div className="text-[12px] text-ink-secondary">
                    {m.theyHave.length > 0 && (
                      <span className="text-success-green font-medium">
                        {m.theyHave.length} card{m.theyHave.length === 1 ? '' : 's'} you want
                      </span>
                    )}
                    {m.theyHave.length > 0 && m.theyWant.length > 0 && ' • '}
                    {m.theyWant.length > 0 && (
                      <span className="text-yellow-dark font-medium">
                        {m.theyWant.length} card{m.theyWant.length === 1 ? '' : 's'} they want
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-[11px] font-bold text-white bg-navy-dark rounded-full px-2.5 py-1 flex-shrink-0">
                  {m.score} match{m.score === 1 ? '' : 'es'}
                </span>
              </button>

              {expanded === m.friend.id && (
                <div className="px-4 pb-4 space-y-4">
                  <button
                    onClick={() =>
                      openProposalBuilder({
                        friend: { userId: m.friend.id, username: m.friend.username }
                      })
                    }
                    className="w-full bg-navy-dark text-yellow font-semibold py-2.5 rounded-xl text-[13px] hover:bg-navy-light transition-colors"
                  >
                    Propose a trade with {m.friend.username}
                  </button>
                  {m.theyHave.length > 0 && (
                    <div>
                      <h4 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-success-green mb-2">
                        <ArrowDownToLine size={12} />
                        {m.friend.username} has — you want
                      </h4>
                      <div className="space-y-1.5">
                        {m.theyHave.map((c) => (
                          <MatchCard key={`${c.cardId}|${c.variant}`} cardItem={c} />
                        ))}
                      </div>
                    </div>
                  )}
                  {m.theyWant.length > 0 && (
                    <div>
                      <h4 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-yellow-dark mb-2">
                        <ArrowUpFromLine size={12} />
                        You have — {m.friend.username} wants
                      </h4>
                      <div className="space-y-1.5">
                        {m.theyWant.map((c) => (
                          <MatchCard key={`${c.cardId}|${c.variant}`} cardItem={c} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MatchesTab;
